import { useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import {
  Alert,
  Box,
  Button,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { defaultStyle } from "components/Modals";
import { createFee, updateFee } from "api/resources/fees";
import {
  Fee,
  FeeCountry,
  feeCountryLabels,
  FeeType,
  feeTypeLabels,
} from "api/models/Fee";

const DATE_FORMAT = "YYYY-MM-DD";
const PRICE_PATTERN = /^\d+([.,]\d{1,2})?$/;

type FeeModalProps = {
  fee?: Fee;
  onSaved: () => void;
  onClose: () => void;
};

const FeeModal = ({ fee, onSaved, onClose }: FeeModalProps) => {
  const [costType, setCostType] = useState<FeeType>(
    fee?.costType ?? "IMBALANCE_TARIFF",
  );
  const [country, setCountry] = useState<FeeCountry | "">(fee?.country ?? "");
  const [effectiveFrom, setEffectiveFrom] = useState<Dayjs | null>(
    fee ? dayjs(fee.effectiveFrom) : null,
  );
  const [effectiveTo, setEffectiveTo] = useState<Dayjs | null>(
    fee ? dayjs(fee.effectiveTo) : null,
  );
  const [price, setPrice] = useState(fee ? String(fee.price) : "");
  const [error, setError] = useState<string | null>(null);

  const isPriceValid = PRICE_PATTERN.test(price.trim());
  const isPeriodValid =
    effectiveFrom?.isValid() &&
    effectiveTo?.isValid() &&
    !effectiveTo.isBefore(effectiveFrom, "day");
  const isComplete = country !== "" && isPeriodValid && isPriceValid;

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const data = {
      costType,
      country: country as FeeCountry,
      effectiveFrom: effectiveFrom.format(DATE_FORMAT),
      effectiveTo: effectiveTo.format(DATE_FORMAT),
      price: price.trim().replace(",", "."),
    };

    (fee ? updateFee(fee.id, data) : createFee(data))
      .then(() => {
        onSaved();
        onClose();
      })
      .catch((err) => {
        setError(err.response?.data?.message ?? "Failed to save the fee");
      });
  };

  return (
    <Box sx={defaultStyle}>
      <Typography variant="h5" component="h5">
        {fee ? "Edit fee" : "Add fee"}
      </Typography>
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Grid container spacing={2} sx={{ mt: "1rem", mb: "2rem" }}>
          <Grid item sm={12}>
            <TextField
              select
              label="Fee"
              size="small"
              required
              value={costType}
              onChange={(event) => setCostType(event.target.value as FeeType)}
              sx={{ width: "100%" }}
            >
              {Object.entries(feeTypeLabels).map(([type, label]) => (
                <MenuItem key={type} value={type}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item sm={12}>
            <TextField
              select
              label="Country"
              size="small"
              required
              value={country}
              onChange={(event) => setCountry(event.target.value as FeeCountry)}
              sx={{ width: "100%" }}
            >
              {Object.entries(feeCountryLabels).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item sm={6}>
            <DatePicker
              label="Valid from"
              value={effectiveFrom}
              onChange={(newValue) => setEffectiveFrom(newValue)}
              slotProps={{ textField: { fullWidth: true, size: "small" } }}
            />
          </Grid>
          <Grid item sm={6}>
            <DatePicker
              label="Valid to"
              value={effectiveTo}
              minDate={effectiveFrom ?? undefined}
              onChange={(newValue) => setEffectiveTo(newValue)}
              slotProps={{ textField: { fullWidth: true, size: "small" } }}
            />
          </Grid>
          <Grid item sm={12}>
            <TextField
              label="Price (€/MWh)"
              size="small"
              required
              value={price}
              onChange={(event) => setPrice(event.target.value)}
              error={price !== "" && !isPriceValid}
              helperText="A non-negative number with up to 2 decimals"
              inputProps={{ inputMode: "decimal" }}
              sx={{ width: "100%" }}
            />
          </Grid>
          {error && (
            <Grid item sm={12}>
              <Alert variant="filled" severity="error">
                {error}
              </Alert>
            </Grid>
          )}
          <Grid item sm={12} sx={{ mt: "1rem" }}>
            <Button
              variant="contained"
              type="submit"
              disabled={!isComplete}
              sx={{ mr: "1rem" }}
            >
              Save
            </Button>
            <Button onClick={onClose}>Cancel</Button>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default FeeModal;
